import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import MiniLoader from "./MiniLoader";

const DynamicAlert = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  itemDescription,
  warningText,
  loading = false,
  confirmText = "تأكيد",
  cancelText = "إلغاء",
  confirmColor = "#dc2626",
  warningColor = "#dc2626",
  input = null,
  inputLabel = "",
  inputPlaceholder = "",
  imageUrl = null,
  imageAlt = "Image",
  showActions = true
}) => {
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (isOpen) {
      setInputValue("");
    }
  }, [isOpen]);
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="modal-content dynamic-alert"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="alert-header">
              <h2>{title}</h2>
              <button className="close-btn" onClick={onClose}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="alert-content">
              <p>{message}</p>
              {(itemName || itemDescription) && (
                <div className="alert-item-info">
                  {itemName && <h3>{itemName}</h3>}
                  {itemDescription && <p>{itemDescription}</p>}
                </div>
              )}
              {imageUrl && (
                <div className="alert-image-container">
                  <img src={imageUrl} alt={imageAlt} className="alert-image" />
                </div>
              )}
              {warningText && <p className="warning-text" style={{ color: warningColor }}>{warningText}</p>}
              {input === "textarea" && (
                <div className="alert-input-container">
                  {inputLabel && <label>{inputLabel}</label>}
                  <textarea 
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={inputPlaceholder}
                    rows={4}
                  />
                </div>
              )}
            </div>
            {showActions && (
              <div className="alert-actions">
                <button
                  className="cancel-btn"
                  onClick={onClose}
                  disabled={loading}
                >
                  {cancelText}
                </button>
                <button
                  className="confirm-btn"
                  onClick={() => onConfirm(inputValue)}
                  disabled={loading}
                  style={{ background: confirmColor }}
                >
                  {loading ? (
                    <MiniLoader color="white" size={20} />
                  ) : (
                    confirmText
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 1rem;
        }

        .modal-content {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          border-radius: 24px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .alert-header {
          padding: 1.5rem;
          border-bottom: 2px solid rgba(226, 232, 240, 0.8);
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #e4e4f1;
          border-top-left-radius: 24px;
          border-top-right-radius: 24px;
        }

        .alert-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #2d3748;
        }

        .close-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: rgba(0, 0, 0, 0.05);
          color: #1e293b;
        }

        .alert-content {
          padding: 1.5rem;
        }

        .alert-content p {
          margin: 0 0 1rem 0;
          color: #4a5568;
        }

        .alert-item-info {
          background: #f8fafc;
          padding: 1rem;
          border-radius: 12px;
          margin: 1rem 0;
        }

        .alert-item-info h3 {
          margin: 0 0 0.5rem 0;
          color: #2d3748;
        }

        .alert-item-info p {
          margin: 0;
          color: #64748b;
        }

        .warning-text {
          font-weight: 600;
        }

        .alert-input-container {
          margin-top: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .alert-input-container label {
          font-weight: 600;
          color: #2d3748;
        }

        .alert-input-container textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-family: inherit;
          resize: vertical;
        }

        .alert-input-container textarea:focus {
          outline: none;
          border-color: #4a90e2;
        }

        .alert-image-container {
          margin-bottom: 1rem;
          text-align: center;
        }

        .alert-image {
          max-width: 100%;
          max-height: 400px;
          border-radius: 12px;
          object-fit: contain;
        }

        .alert-actions {
          padding: 1.5rem;
          border-top: 2px solid rgba(226, 232, 240, 0.8);
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          background: #e4e4f1;
          border-bottom-left-radius: 24px;
          border-bottom-right-radius: 24px;
        }

        .alert-actions button {
          padding: 0.75rem 1.5rem;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .alert-actions .cancel-btn {
          background: #f1f5f9;
          color: #2d3748;
          border: none;
          font-family: "Zain", sans-serif;
        }

        .alert-actions .confirm-btn {
          color: white;
          border: none;
          font-family: "Zain", sans-serif;
        }

        .alert-actions button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .modal-content {
            max-width: 95%;
            margin: 1rem;
          }

          .alert-header {
            padding: 1rem;
          }

          .alert-header h2 {
            font-size: 1.25rem;
          }

          .alert-content {
            padding: 1rem;
          }

          .alert-actions {
            padding: 1rem;
            flex-direction: column;
          }

          .alert-actions button {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .modal-content {
            max-width: 100%;
            margin: 0.5rem;
            border-radius: 16px;
          }

          .alert-header {
            padding: 0.75rem;
          }

          .alert-header h2 {
            font-size: 1.1rem;
          }

          .alert-content {
            padding: 0.75rem;
          }

          .alert-actions {
            padding: 0.75rem;
          }
        }
      `}</style>
    </AnimatePresence>
  );
};

export default DynamicAlert;
